import { addDays, differenceInCalendarDays, parseISO } from 'date-fns';
import { Constants } from '../../constants';
import Colours from '../../../design-system/colours';
import { DailyData } from '../../model/dto/daily-data';
import { DateRange } from '../../model/type/date-range.type';
import {
  isDirectionalSymptom,
  SYMPTOM_CATALOGUE,
  SYMPTOM_CATEGORY_LABEL_KEYS,
  SYMPTOM_CATEGORY_ORDER,
  SYMPTOM_ORDER,
  SymptomKey,
  getSymptomValueLabels,
} from '../../symptom-catalogue';
import { CyclePhaseBoundary } from '../../model/dto/hormone-summary';
import {
  BuildDayColumnModelInput,
  CyclePhaseBand,
  CyclePhaseBandId,
  CyclePhaseBands,
  CyclePhaseDays,
  DayColumn,
  HormoneTrendsDayColumnModel,
  HormoneSeriesId,
  HormoneTrendsSeriesDefinition,
  HormoneTrendsSeriesId,
  HormoneTrendsSeriesStyle,
  PhaseRange,
  SymptomCategoryGroup,
  SymptomRowBase,
  SymptomRow,
} from '../../model/type/hormone-trends.type';
import ChartUtils, { BLEEDING_VALUES } from './chart-utils';
import DateUtils from './date-utils';
import {
  getIntensity,
  getSymptomScoreWithFallback,
  isReported,
  toArray,
} from './symptom-intensity-utils';
import { SymptomIntensity } from '../../model/type/symptom-intensity.type';

export const HORMONE_TRENDS_AXIS_WIDTHS: Record<HormoneTrendsSeriesId, number> =
  {
    lh: 56,
    pg: 56,
    lr_e3g: 64,
    bbt: 48,
  };

export const BBT_DEFAULT_DOMAIN: [number, number] = [95, 100];

export const BBT_EXTENDED_DOMAIN: [number, number] = [95, 105];

export const HORMONE_TRENDS_SERIES: Record<
  HormoneTrendsSeriesId,
  HormoneTrendsSeriesDefinition
> = {
  lh: {
    id: 'lh',
    axisId: 'lhAxis',
    axisOrientation: 'left',
    axisWidth: HORMONE_TRENDS_AXIS_WIDTHS.lh,
    displayName: 'LH',
    unit: Constants.LH_UNIT_OF_MEASURE,
    domain: [Constants.LH_MIN, Constants.LH_MAX + 10],
  },
  pg: {
    id: 'pg',
    axisId: 'pgAxis',
    axisOrientation: 'left',
    axisWidth: HORMONE_TRENDS_AXIS_WIDTHS.pg,
    displayName: 'PdG',
    unit: Constants.PG_UNIT_OF_MEASURE,
    domain: [Constants.PG_MIN, Constants.PG_MAX + 5],
  },
  lr_e3g: {
    id: 'lr_e3g',
    axisId: 'lrE3gAxis',
    axisOrientation: 'right',
    axisWidth: HORMONE_TRENDS_AXIS_WIDTHS.lr_e3g,
    displayName: 'E3G',
    unit: Constants.E3G_UNIT_OF_MEASURE,
    domain: [Constants.LR_E3G_MIN, Constants.LR_E3G_CHART_MAX],
  },
  bbt: {
    id: 'bbt',
    axisId: 'temperatureAxis',
    axisOrientation: 'right',
    axisWidth: HORMONE_TRENDS_AXIS_WIDTHS.bbt,
    displayName: 'BBT',
    unit: Constants.TEMPERATURE_UNIT_OF_MEASURE,
    domain: BBT_DEFAULT_DOMAIN,
    isOverlay: true,
  },
};

export const HORMONE_TRENDS_SERIES_STYLES: Record<
  HormoneTrendsSeriesId,
  HormoneTrendsSeriesStyle
> = {
  lh: { colour: Colours.LIME_GREEN },
  pg: { colour: Colours.OV_HORMONE_PDG },
  lr_e3g: { colour: Colours.OV_HORMONE_E3G },
  bbt: { colour: Colours.OV_HORMONE_BBT, strokeDasharray: '4 3' },
};

export const HORMONE_TRENDS_AXIS_COLOURS: Partial<
  Record<HormoneTrendsSeriesId, string>
> = {
  lh: Colours.OV_BASE,
};

export const getHormoneAxisColour = (seriesId: HormoneTrendsSeriesId): string =>
  HORMONE_TRENDS_AXIS_COLOURS[seriesId] ??
  HORMONE_TRENDS_SERIES_STYLES[seriesId].colour;

export const HORMONE_TRENDS_SERIES_ORDER: HormoneTrendsSeriesId[] = [
  'lh',
  'pg',
  'lr_e3g',
  'bbt',
];

export const HORMONE_SERIES_ORDER = HORMONE_TRENDS_SERIES_ORDER.filter(
  (seriesId) => !HORMONE_TRENDS_SERIES[seriesId].isOverlay
) as HormoneSeriesId[];

export const getVisibleHormoneSeries = (
  focusedSeriesId: HormoneTrendsSeriesId | null,
  bbtEnabled: boolean
): HormoneTrendsSeriesId[] => {
  const baseSeries =
    focusedSeriesId === null || HORMONE_TRENDS_SERIES[focusedSeriesId].isOverlay
      ? HORMONE_SERIES_ORDER
      : [focusedSeriesId];

  return bbtEnabled ? [...baseSeries, 'bbt'] : baseSeries;
};

export const getHormoneAxisOrientation = (
  seriesId: HormoneTrendsSeriesId,
  focusedSeriesId: HormoneTrendsSeriesId | null
): 'left' | 'right' => {
  if (seriesId === 'lr_e3g' && focusedSeriesId === 'lr_e3g') {
    return 'left';
  }

  return HORMONE_TRENDS_SERIES[seriesId].axisOrientation;
};

const sumAxisWidths = (
  orientation: 'left' | 'right',
  focusedSeriesId: HormoneTrendsSeriesId | null = null
): number =>
  HORMONE_TRENDS_SERIES_ORDER.map((id) => HORMONE_TRENDS_SERIES[id])
    .filter(
      (series) =>
        getHormoneAxisOrientation(series.id, focusedSeriesId) === orientation
    )
    .reduce((total, series) => total + series.axisWidth, 0);

export const HORMONE_TRENDS_LEFT_GUTTER_WIDTH = sumAxisWidths('left');

export const HORMONE_TRENDS_RIGHT_GUTTER_WIDTH = sumAxisWidths('right');

export const getLeftGutterWidth = (
  focusedSeriesId: HormoneTrendsSeriesId | null
): number => sumAxisWidths('left', focusedSeriesId);

export const getRightGutterWidth = (
  focusedSeriesId: HormoneTrendsSeriesId | null
): number => sumAxisWidths('right', focusedSeriesId);

export const HORMONE_TRENDS_MIN_DAY_COLUMN_WIDTH = 22;

export const HORMONE_TRENDS_DATE_TICK_LABEL_WIDTH = 44;

export const MIN_CYCLE_SCAN_DAYS = 5;

export const getScanDayCount = (dailyDataArray: DailyData[]): number =>
  dailyDataArray.filter((dailyData) => !!dailyData.scans?.length).length;

export const getBbtDomain = (columns: DayColumn[]): [number, number] =>
  columns.some(
    (column) => (column.dailyData?.journal_entry?.temperature ?? 0) > 100
  )
    ? BBT_EXTENDED_DOMAIN
    : BBT_DEFAULT_DOMAIN;

export const CYCLE_PHASE_BAND_LABEL_KEYS: Record<CyclePhaseBandId, string> = {
  period: 'hormoneTrends.phaseBands.period',
  follicular: 'hormoneTrends.phaseBands.follicular',
  ovulation_window: 'hormoneTrends.phaseBands.ovulationWindow',
  luteal: 'hormoneTrends.phaseBands.luteal',
};

const CYCLE_PHASE_BAND_ORDER: CyclePhaseBandId[] = [
  'period',
  'follicular',
  'ovulation_window',
  'luteal',
];

const isValidDate = (date?: Date): boolean => !!date && !isNaN(date.getTime());

const parseDay = (day?: string | null): Date | null => {
  if (!day) {
    return null;
  }

  const parsed = parseISO(day);

  return isValidDate(parsed) ? parsed : null;
};

const buildColumns = (
  { startDate, endDate }: DateRange,
  dailyDataArray: DailyData[],
  cycleStartDate: Date | null
): DayColumn[] => {
  if (!isValidDate(startDate) || !isValidDate(endDate)) {
    return [];
  }

  const windowLength = ChartUtils.getWindowLength(startDate, endDate);

  if (windowLength <= 0) {
    return [];
  }

  const dailyDataByDay = new Map<string, DailyData>();
  dailyDataArray.forEach((dailyData) => {
    if (dailyData.day) {
      dailyDataByDay.set(dailyData.day, dailyData);
    }
  });

  const columns: DayColumn[] = [];

  for (let index = 0; index < windowLength; index++) {
    const date = addDays(startDate, index);
    const key = DateUtils.getDbDateTag(date);
    const dailyData = dailyDataByDay.get(key);

    columns.push({
      key,
      index,
      date,
      dateLabel: DateUtils.formatLocaleDate(date),
      cycleDay: cycleStartDate
        ? differenceInCalendarDays(date, cycleStartDate) + 1
        : undefined,
      dailyData,
      isBleeding: BLEEDING_VALUES.includes(dailyData?.bleeding ?? ''),
    });
  }

  return columns;
};

const toBand = (
  phase: CyclePhaseBandId,
  startIndex: number,
  endIndex: number,
  columnCount: number
): CyclePhaseBand | null => {
  const clampedStart = Math.max(startIndex, 0);
  const clampedEnd = Math.min(endIndex, columnCount - 1);

  if (clampedStart > clampedEnd) {
    return null;
  }

  return {
    phase,
    labelKey: CYCLE_PHASE_BAND_LABEL_KEYS[phase],
    startIndex: clampedStart,
    endIndex: clampedEnd,
  };
};

const firstBleedingRun = (columns: DayColumn[]): PhaseRange | null => {
  const startIndex = columns.findIndex((column) => column.isBleeding);

  if (startIndex === -1) {
    return null;
  }

  let endIndex = startIndex;

  while (endIndex + 1 < columns.length && columns[endIndex + 1].isBleeding) {
    endIndex++;
  }

  return { startIndex, endIndex };
};

const toPeriodRange = (
  bleedingRun: PhaseRange | null,
  spans: { phase: CyclePhaseBandId; range: PhaseRange }[]
): PhaseRange | null => {
  if (!bleedingRun || spans.length === 0) {
    return bleedingRun;
  }

  const openingPhase = spans.reduce((earliest, span) =>
    span.range.startIndex < earliest.range.startIndex ? span : earliest
  ).range;

  if (bleedingRun.startIndex > openingPhase.endIndex) {
    return null;
  }

  return {
    startIndex: Math.min(bleedingRun.startIndex, openingPhase.startIndex),
    endIndex: bleedingRun.endIndex,
  };
};

const toDisjointBands = (
  ranges: { phase: CyclePhaseBandId; range: PhaseRange }[],
  columnCount: number
): CyclePhaseBand[] => {
  const bands: CyclePhaseBand[] = [];
  let previousEnd: number | null = null;

  [...ranges]
    .sort(
      (a, b) =>
        CYCLE_PHASE_BAND_ORDER.indexOf(a.phase) -
        CYCLE_PHASE_BAND_ORDER.indexOf(b.phase)
    )
    .forEach(({ phase, range }) => {
      const band = toBand(
        phase,
        previousEnd === null
          ? range.startIndex
          : Math.max(range.startIndex, previousEnd + 1),
        range.endIndex,
        columnCount
      );

      if (band) {
        bands.push(band);
        previousEnd = band.endIndex;
      }
    });

  return bands;
};

export const derivePhaseBands = (
  columns: DayColumn[],
  phaseDays: CyclePhaseDays = {}
): CyclePhaseBands => {
  if (columns.length === 0) {
    return { bands: [], spans: [] };
  }

  const windowStart = columns[0].date;
  const indexOf = (day?: string | null): number | null => {
    const date = parseDay(day);

    return date ? differenceInCalendarDays(date, windowStart) : null;
  };
  const rangeOf = (boundary?: CyclePhaseBoundary | null): PhaseRange | null => {
    const startIndex = indexOf(boundary?.start_day);
    const endIndex = indexOf(boundary?.end_day);

    return startIndex !== null && endIndex !== null
      ? { startIndex, endIndex }
      : null;
  };

  const serverRanges: [CyclePhaseBandId, PhaseRange | null][] = [
    ['follicular', rangeOf(phaseDays.follicular)],
    ['ovulation_window', rangeOf(phaseDays.ovulation)],
    ['luteal', rangeOf(phaseDays.luteal)],
  ];
  const spans = serverRanges.flatMap(([phase, range]) =>
    range ? [{ phase, range }] : []
  );
  const period = toPeriodRange(firstBleedingRun(columns), spans);

  return {
    bands: toDisjointBands(
      period
        ? [{ phase: 'period' as CyclePhaseBandId, range: period }, ...spans]
        : spans,
      columns.length
    ),
    spans: spans.flatMap(({ phase, range }) => {
      const band = toBand(
        phase,
        range.startIndex,
        range.endIndex,
        columns.length
      );

      return band ? [band] : [];
    }),
  };
};

const buildSymptomRow = (
  symptom: SymptomKey,
  columns: DayColumn[]
): SymptomRow => {
  const definition = SYMPTOM_CATALOGUE[symptom];
  const rawValues = columns.map(
    (column) => column.dailyData?.journal_entry?.[symptom]
  );
  const scores = columns.map((column, index) =>
    getSymptomScoreWithFallback(
      column.dailyData?.journal_entry?.symptom_scores,
      symptom,
      rawValues[index]
    )
  );
  const base: SymptomRowBase = {
    category: definition.category,
    labelKey: definition.labelKey,
    valueLabels: rawValues.map((rawValue, index) => {
      const rawLabels = toArray(rawValue).filter(
        (value): value is string =>
          typeof value === 'string' && value.length > 0
      );
      const resolvedScore = getIntensity(symptom, scores[index]).score;

      if (rawLabels.length > 0) {
        return rawLabels;
      }

      return typeof resolvedScore === 'number'
        ? getSymptomValueLabels(symptom, resolvedScore)
        : null;
    }),
  };

  if (isDirectionalSymptom(symptom)) {
    const directionalSymptom = symptom;

    return {
      ...base,
      logic: 'directional',
      symptom: directionalSymptom,
      cells: scores.map((score) => getIntensity(directionalSymptom, score)),
    };
  }

  const severitySymptom = symptom;

  return {
    ...base,
    logic: 'severity',
    symptom: severitySymptom,
    cells: scores.map((score) => getIntensity(severitySymptom, score)),
  };
};

const buildSymptomRows = (columns: DayColumn[]): SymptomRow[] =>
  SYMPTOM_ORDER.map((symptom) => buildSymptomRow(symptom, columns)).filter(
    (row) => (row.cells as SymptomIntensity[]).some(isReported)
  );

export const buildDayColumnModel = ({
  dateRange,
  rangeMode,
  selectedCycle,
  dailyDataArray = [],
  phaseDays,
}: BuildDayColumnModelInput): HormoneTrendsDayColumnModel => {
  const cycle = rangeMode === 'cycle' ? selectedCycle : undefined;
  const columns = buildColumns(
    dateRange,
    dailyDataArray,
    cycle ? parseDay(cycle.start_day) : null
  );

  const { bands, spans } = cycle
    ? derivePhaseBands(columns, phaseDays)
    : { bands: [], spans: [] };

  return {
    columns,
    phaseBands: bands,
    phaseSpans: spans,
    symptomRows: buildSymptomRows(columns),
  };
};

export const groupSymptomRowsByCategory = (
  rows: SymptomRow[]
): SymptomCategoryGroup[] =>
  SYMPTOM_CATEGORY_ORDER.map((category) => ({
    category,
    labelKey: SYMPTOM_CATEGORY_LABEL_KEYS[category],
    rows: rows.filter((row) => row.category === category),
  })).filter((group) => group.rows.length > 0);
